/* ============================================================
   Liquid+ Product Page — Vandori
   Vanilla JS. No dependencies. Scoped per-section, idempotent,
   and Theme-Editor aware (re-inits on shopify:section:load).
   Progressive enhancement only: the page is fully readable and
   the purchase flow (Kaching Bundles app block / native product
   form) works without this file.
   ============================================================ */
(function () {
  'use strict';

  // Guard: the loader snippet is included by every lp-* section, so this
  // file may be requested by several <script> tags. Execute the setup once.
  if (window.__lpProductInit) return;
  window.__lpProductInit = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Reveal on scroll ---- */
  function initReveal(scope) {
    var els = scope.querySelectorAll('[data-lp-reveal]:not([data-lp-reveal-init])');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); el.setAttribute('data-lp-reveal-init', ''); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    els.forEach(function (el) { el.setAttribute('data-lp-reveal-init', ''); io.observe(el); });
  }

  /* ---- Animated stats + progress bars ---- */
  function initStats(scope) {
    var band = scope.querySelector('[data-lp-stats]:not([data-lp-stats-init])');
    if (!band) return;
    band.setAttribute('data-lp-stats-init', '');

    function run() {
      // progress bars
      band.querySelectorAll('[data-lp-bar]').forEach(function (bar) {
        bar.style.width = (bar.getAttribute('data-lp-bar') || '0') + '%';
      });
      // counters
      var counters = band.querySelectorAll('[data-lp-count]');
      if (reduceMotion) {
        counters.forEach(function (c) { c.textContent = format(c) + (c.getAttribute('data-suffix') || ''); });
        return;
      }
      var dur = 1500, t0 = performance.now();
      var ease = function (t) { return 1 - Math.pow(1 - t, 3); };
      function step(now) {
        var p = Math.min(1, (now - t0) / dur), e = ease(p);
        counters.forEach(function (c) {
          var target = parseFloat(c.getAttribute('data-lp-count')) || 0;
          c.textContent = new Intl.NumberFormat('nl-NL').format(Math.round(target * e)) + (c.getAttribute('data-suffix') || '');
        });
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }
    function format(c) {
      var target = parseFloat(c.getAttribute('data-lp-count')) || 0;
      return new Intl.NumberFormat('nl-NL').format(Math.round(target));
    }

    if (reduceMotion || !('IntersectionObserver' in window)) { run(); return; }
    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { run(); io.disconnect(); }
    }, { threshold: 0.35 });
    io.observe(band);
  }

  /* ---- Feature explorer (tabs) ---- */
  function initExplorer(scope) {
    scope.querySelectorAll('[data-lp-explorer]:not([data-lp-explorer-init])').forEach(function (exp) {
      exp.setAttribute('data-lp-explorer-init', '');
      var tabs = exp.querySelectorAll('[data-lp-tab]');
      var panes = exp.querySelectorAll('[data-lp-pane]');
      function activate(idx) {
        tabs.forEach(function (t, i) { t.setAttribute('aria-selected', i === idx ? 'true' : 'false'); });
        panes.forEach(function (p, i) { p.hidden = i !== idx; });
      }
      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { activate(i); });
        tab.addEventListener('keydown', function (ev) {
          if (ev.key === 'ArrowDown' || ev.key === 'ArrowRight') { ev.preventDefault(); (tabs[i + 1] || tabs[0]).focus(); }
          if (ev.key === 'ArrowUp' || ev.key === 'ArrowLeft') { ev.preventDefault(); (tabs[i - 1] || tabs[tabs.length - 1]).focus(); }
        });
      });
      activate(0);
    });
  }

  /* ---- FAQ accordion ---- */
  function initFaq(scope) {
    scope.querySelectorAll('[data-lp-faq]:not([data-lp-faq-init])').forEach(function (faq) {
      faq.setAttribute('data-lp-faq-init', '');
      var items = faq.querySelectorAll('[data-lp-faq-item]');
      items.forEach(function (item) {
        var btn = item.querySelector('.lp-faq-q');
        var panel = item.querySelector('.lp-faq-panel');
        if (!btn || !panel) return;
        btn.addEventListener('click', function () {
          var open = btn.getAttribute('aria-expanded') === 'true';
          // close siblings (single-open accordion, matches design)
          items.forEach(function (other) {
            var ob = other.querySelector('.lp-faq-q');
            var op = other.querySelector('.lp-faq-panel');
            if (ob && op) { ob.setAttribute('aria-expanded', 'false'); op.style.maxHeight = '0px'; op.style.opacity = '0'; }
          });
          if (!open) {
            btn.setAttribute('aria-expanded', 'true');
            panel.style.maxHeight = panel.scrollHeight + 'px';
            panel.style.opacity = '1';
          }
        });
      });
    });
  }

  /* ---- Hero gallery (thumbnail -> main swap) ---- */
  function initGallery(scope) {
    scope.querySelectorAll('[data-lp-gallery]:not([data-lp-gallery-init])').forEach(function (g) {
      g.setAttribute('data-lp-gallery-init', '');
      var main = g.querySelector('[data-lp-gallery-main]') || g.querySelector('.lp-hero-main img');
      if (!main) return;
      g.querySelectorAll('[data-lp-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.getAttribute('data-src');
          var srcset = thumb.getAttribute('data-srcset');
          var alt = thumb.getAttribute('data-alt') || '';
          if (src) main.src = src;
          if (srcset) main.srcset = srcset; else main.removeAttribute('srcset');
          main.alt = alt;
          g.querySelectorAll('[data-lp-thumb]').forEach(function (t) { t.setAttribute('aria-current', 'false'); });
          thumb.setAttribute('aria-current', 'true');
        });
      });
    });
  }

  /* ---- Sticky buy bar (appears once hero is scrolled past) ---- */
  function initSticky(scope) {
    var bar = scope.querySelector('[data-lp-sticky]:not([data-lp-sticky-init])');
    if (!bar) return;
    bar.setAttribute('data-lp-sticky-init', '');
    var anchorId = bar.getAttribute('data-lp-watch') || 'lp-hero';
    var hideId = bar.getAttribute('data-lp-hide') || '';
    var hero = document.getElementById(anchorId);
    if (!hero || !('IntersectionObserver' in window)) { return; }
    var pastHero = false, atEnd = false;
    function apply() { bar.classList.toggle('is-visible', pastHero && !atEnd); }
    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      pastHero = !e.isIntersecting && e.boundingClientRect.top < 0;
      apply();
    }, { threshold: 0 });
    io.observe(hero);
    if (hideId) {
      var endEl = document.getElementById(hideId);
      if (endEl) {
        var io2 = new IntersectionObserver(function (entries) {
          atEnd = entries[0].isIntersecting;
          apply();
        }, { threshold: 0 });
        io2.observe(endEl);
      }
    }
  }

  /* ---- Master init for a scope (document or a single section) ---- */
  function initAll(scope) {
    if (!scope) return;
    initReveal(scope);
    initStats(scope);
    initExplorer(scope);
    initFaq(scope);
    initGallery(scope);
    initSticky(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* ---- Theme Editor: re-init a section when (re)loaded ---- */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.lp')) { initAll(e.target); }
    else if (e.target) { initAll(e.target); }
  });
  // Reflow open FAQ panels if a section is reselected
  document.addEventListener('shopify:section:select', function (e) {
    if (!e.target) return;
    e.target.querySelectorAll('.lp-faq-q[aria-expanded="true"]').forEach(function (btn) {
      var panel = btn.parentElement && btn.parentElement.querySelector('.lp-faq-panel');
      if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
    });
  });
})();
